import React, { useEffect, useState, useMemo } from "react";

import NoDataFound from "../../common/NoDataFound";
import { calculatePercentagestats } from "../../../utils/generalLiveCenter";
import MatchStats from "../../common/MatchStats";
import { loadStyles } from "../../../utils/cssLoader";

const Stats = ({ data, loadCSS, dataClient = false, title }) => {
  const [clientData, setClientData] = useState(null);
  const [activeHalf, setActiveHalf] = useState("total");
  const [activeQuarter, setActiveQuarter] = useState("first_quarter");

  useEffect(() => {
    if (!dataClient) return;

    const container = document.querySelector(".microservice-cont");
    const raw = container?.getAttribute("data-listingdata");
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw)?.data?.data;
      setClientData(parsed || null);
    } catch (err) {
      console.error("Failed to parse data-listingdata:", err);
    }
  }, [dataClient]);

  const sourceData = dataClient ? clientData : data;

  // Check if this is basketball quarter-based data
  const isBasketballData = sourceData && (
    sourceData.first_quarter ||
    sourceData.second_quarter ||
    sourceData.third_quarter ||
    sourceData.fourth_quarter
  );

  // Check if this is football half-based data
  const isFootballData = sourceData && (
    sourceData.first_half ||
    sourceData.second_half ||
    sourceData.total
  );

  // Process stats from half/quarter data
  const processStats = (periodData) => {
    if (!periodData || typeof periodData !== "object") return [];
    return Object.entries(periodData)
      .filter(([key, value]) =>
        value &&
        typeof value === "object" &&
        "ht" in value &&
        "at" in value &&
        key !== "total" &&
        (value.ht !== 0 || value.at !== 0)
      )
      .sort(([, a], [, b]) => (a.sort || 999) - (b.sort || 999))
      .map(([type, { ht, at }]) => ({
        type,
        ht,
        at,
        htPercent: calculatePercentagestats(ht, at),
        atPercent: calculatePercentagestats(at, ht),
      }));
  };

  // Legacy stats processing
  const legacyStats = useMemo(() => {
    if (!sourceData || typeof sourceData !== "object" || isFootballData || isBasketballData) return [];

    return Object.entries(sourceData).map(([type, { ht, at } = {}]) => ({
      type,
      ht,
      at,
      htPercent: calculatePercentagestats(ht, at),
      atPercent: calculatePercentagestats(at, ht),
    }));
  }, [sourceData, isFootballData, isBasketballData]);

  useEffect(() => {
    if (loadCSS === "true") {
      loadStyles("matchstat");
    }
  }, [loadCSS]);

  // Basketball quarter-based rendering
  if (isBasketballData) {
    const quarters = [
      { key: "first_quarter", label: "1st Quarter" },
      { key: "second_quarter", label: "2nd Quarter" },
      { key: "third_quarter", label: "3rd Quarter" },
      { key: "fourth_quarter", label: "4th Quarter" },
    ];

    const currentQuarterData = sourceData[activeQuarter];
    let basketballStats = [];

    if (currentQuarterData) {
      // Process scoring stats
      if (currentQuarterData.scoring) {
        const scoring = currentQuarterData.scoring;
        const scoringKeys = [
          "field_goals_attempted", "field_goals_made", "field_goals_percentage",
          "two_pointers_attempted", "two_pointers_made", "two_pointers_percentage",
          "three_pointers_attempted", "three_pointers_made", "three_pointers_percentage",
          "free_throws_attempted", "free_throws_made", "free_throws_percentage"
        ];
        scoringKeys.forEach(key => {
          if (scoring[key]) {
            basketballStats.push({
              type: key,
              ht: scoring[key]?.ht || 0,
              at: scoring[key]?.at || 0,
              htPercent: calculatePercentagestats(scoring[key]?.ht || 0, scoring[key]?.at || 0),
              atPercent: calculatePercentagestats(scoring[key]?.at || 0, scoring[key]?.ht || 0),
            });
          }
        });
      }

      // Process rebounds stats
      if (currentQuarterData.rebounds) {
        const rebounds = currentQuarterData.rebounds;
        const reboundKeys = ["rebounds_offensive", "rebounds_defensive", "total_rebounds"];
        reboundKeys.forEach(key => {
          if (rebounds[key]) {
            basketballStats.push({
              type: key,
              ht: rebounds[key]?.ht || 0,
              at: rebounds[key]?.at || 0,
              htPercent: calculatePercentagestats(rebounds[key]?.ht || 0, rebounds[key]?.at || 0),
              atPercent: calculatePercentagestats(rebounds[key]?.at || 0, rebounds[key]?.ht || 0),
            });
          }
        });
      }

      // Process other stats
      if (currentQuarterData.other) {
        const other = currentQuarterData.other;
        const otherKeys = ["assists", "blocks", "turnovers", "steals", "fouls_personal", "points"];
        otherKeys.forEach(key => {
          if (other[key]) {
            basketballStats.push({
              type: key,
              ht: other[key]?.ht || 0,
              at: other[key]?.at || 0,
              htPercent: calculatePercentagestats(other[key]?.ht || 0, other[key]?.at || 0),
              atPercent: calculatePercentagestats(other[key]?.at || 0, other[key]?.ht || 0),
            });
          }
        });
      }
    }

    return (
      <div className="stats_list_wrp">
        {title && <h1>{title}</h1>}
        <div className="quarter-tabs">
          {quarters.map(quarter => (
            <button
              key={quarter.key}
              className={`quarter-tab ${activeQuarter === quarter.key ? "active" : ""}`}
              onClick={() => setActiveQuarter(quarter.key)}
            >
              {quarter.label}
            </button>
          ))}
        </div>
        <div className="stats-holder">
          {basketballStats.length > 0 ? (
            <MatchStats stats={basketballStats} />
          ) : (
            <NoDataFound />
          )}
        </div>
      </div>
    );
  }

  // Football half-based rendering
  if (isFootballData) {
    const halves = [
      { key: "total", label: "All" },
      { key: "first_half", label: "First Half" },
      { key: "second_half", label: "Second Half" },
    ];

    const currentHalfData = sourceData[activeHalf];
    const footballStats = processStats(currentHalfData);

    return (
      <div className="stats_list_wrp">
        {title && <h1>{title}</h1>}
        <div className="quarter-tabs">
          {halves.map(half => (
            <button
              key={half.key}
              className={`quarter-tab ${activeHalf === half.key ? "active" : ""}`}
              onClick={() => setActiveHalf(half.key)}
            >
              {half.label}
            </button>
          ))}
        </div>
        <div className="stats-holder">
          {footballStats.length > 0 ? (
            <MatchStats stats={footballStats} />
          ) : (
            <NoDataFound />
          )}
        </div>
      </div>
    );
  }

  // Legacy format rendering
  return (
    <div className="stats_list_wrp">
      {title && <h1>{title}</h1>}
      <div className="stats-holder">
        {legacyStats.length > 0 ? <MatchStats stats={legacyStats} /> : <NoDataFound />}
      </div>
    </div>
  );
};

export default Stats;
